import { useEffect, useState } from "react";
import { Users, Wallet, ShoppingBag, Package2, TrendingUp, ArrowUpRight, AlertCircle, Activity, Inbox } from "lucide-react";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatRupiah, formatNumber } from "@/lib/dummyData";
import { AdminAPI } from "@/lib/api";

const pieColors = ["#2D5A27", "#D48C45", "#85A37E", "#A94C38", "#4A7A8C"];
const MONTHS_ID = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];

// Ubah 'YYYY-MM' menjadi label bulan ringkas (mis. "Feb").
const ymLabel = (ym) => {
  const m = Number(String(ym).split("-")[1]);
  return MONTHS_ID[(m - 1 + 12) % 12] || ym;
};
const toJuta = (v) => Math.round((Number(v) / 1_000_000) * 10) / 10;

// Placeholder saat sebuah kartu belum punya data nyata.
const EmptyState = ({ height = "h-56", note = "Belum ada data" }) => (
  <div className={`${height} grid place-items-center text-center`}>
    <div>
      <Inbox className="mx-auto text-ink/30" size={30} />
      <p className="text-sm text-ink/55 mt-2">{note}</p>
    </div>
  </div>
);

export default function AdminDashboard() {
  const [kpi, setKpi] = useState(null);
  const [orders, setOrders] = useState([]);
  const [logs, setLogs] = useState([]);
  const [report, setReport] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let done = 0;
    const finish = () => { done += 1; if (done >= 4) setLoaded(true); };
    AdminAPI.dashboard().then(setKpi).catch(() => {}).finally(finish);
    AdminAPI.orders().then((o) => setOrders(o || [])).catch(() => {}).finally(finish);
    AdminAPI.auditLogs().then((l) => setLogs(l || [])).catch(() => {}).finally(finish);
    AdminAPI.reports().then(setReport).catch(() => {}).finally(finish);
  }, []);

  // --- Penjualan & Simpanan (6 bulan terakhir, dalam juta) ---
  const salesMap = Object.fromEntries((report?.monthly || []).map((m) => [m.ym, Number(m.gmv)]));
  const savingsMap = Object.fromEntries((report?.savings_monthly || []).map((m) => [m.ym, Number(m.total)]));
  const monthKeys = Array.from(new Set([...Object.keys(salesMap), ...Object.keys(savingsMap)])).sort().slice(-6);
  const salesSavings = monthKeys.map((ym) => ({
    month: ymLabel(ym),
    penjualan: toJuta(salesMap[ym] || 0),
    simpanan: toJuta(savingsMap[ym] || 0),
  }));
  const hasSalesSavings = salesSavings.some((d) => d.penjualan > 0 || d.simpanan > 0);

  // --- Distribusi kategori (30 hari, berdasarkan nilai penjualan) ---
  const catData = (report?.categories || [])
    .map((c) => ({ name: c.name, value: Number(c.value) }))
    .filter((c) => c.value > 0);
  const hasCat = catData.length > 0;
  const catTotal = catData.reduce((s, c) => s + c.value, 0) || 1;

  // --- Pertumbuhan anggota (kumulatif per bulan, 6 bulan terakhir) ---
  const growthRaw = report?.member_growth || [];
  let cum = 0;
  const memberGrowth = growthRaw.map((g) => { cum += Number(g.baru); return { month: ymLabel(g.ym), anggota: cum }; }).slice(-6);
  const hasGrowth = memberGrowth.length > 0;

  // --- Pesanan terbaru (data nyata, tanpa fallback dummy) ---
  const recentOrders = (orders || []).slice(0, 5).map((o) => ({
    id: o.order_number, member: o.buyer_name || "-", status: o.status,
    payment: o.payment_method, total: Number(o.total),
  }));

  // --- Aktivitas terkini (audit log nyata) ---
  const activity = (logs || []).slice(0, 5).map((l) => ({
    actor: l.actor_name || "Sistem", role: l.actor_role || "-",
    action: l.action_type, time: new Date(l.created_at).toLocaleString("id-ID"),
  }));

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-amber-warm font-semibold">Dasbor Manajemen</div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold text-ink tracking-tight mt-1">Selamat datang 🌱</h1>
          <p className="text-ink/75 mt-1.5 text-sm">
            {kpi ? `${kpi.pending_shops || 0} gerai menunggu persetujuan · ${kpi.products_count || 0} produk aktif.` : "Ringkasan operasional koperasi."}
          </p>
        </div>
        <div className="flex gap-2">
          <Button className="rounded-lg bg-forest hover:bg-forest-dark text-white" data-testid="admin-export-button">Ekspor Laporan</Button>
        </div>
      </div>

      {/* KPI tiles — data nyata dari /admin/dashboard */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={Users} label="Total Anggota" value={kpi ? formatNumber(kpi.total_members) : "—"} delta="terdaftar" />
        <Kpi icon={TrendingUp} label="GMV (Nilai Transaksi)" value={kpi ? formatRupiah(kpi.gmv) : "—"} delta="pesanan terbayar" />
        <Kpi icon={ShoppingBag} label="Pesanan Aktif" value={kpi ? kpi.active_orders : "—"} delta={`${kpi ? kpi.pending_shops : 0} gerai pending`} warning />
        <Kpi icon={Package2} label="Total Produk" value={kpi ? kpi.products_count : "—"} delta="katalog aktif" />
      </div>

      {/* Penjualan & Simpanan + Distribusi Kategori */}
      <div className="grid lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2 rounded-xl border-stone-soft p-6 bg-white">
          <div className="flex items-center justify-between mb-1">
            <div>
              <h3 className="font-display font-semibold text-ink">Penjualan & Simpanan</h3>
              <p className="text-xs text-ink/55">6 bulan terakhir · dalam juta Rupiah</p>
            </div>
          </div>
          <div className="mt-3">
            {!loaded ? <EmptyState height="h-72" note="Memuat…" /> : !hasSalesSavings ? (
              <EmptyState height="h-72" note="Belum ada penjualan atau simpanan" />
            ) : (
              <div className="h-72">
                <ResponsiveContainer>
                  <LineChart data={salesSavings}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E6E2D8" vertical={false} />
                    <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#889285" }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#889285" }} />
                    <Tooltip content={({ active, payload, label }) => active && payload?.length ? (
                      <div className="bg-white border border-stone-soft rounded-lg px-3 py-2 text-xs shadow-md">
                        <div className="font-semibold mb-1">{label}</div>
                        {payload.map((p) => <div key={p.dataKey} style={{ color: p.color }}>{p.dataKey}: Rp {p.value} jt</div>)}
                      </div>
                    ) : null} />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="penjualan" stroke="#2D5A27" strokeWidth={2.5} dot={{ fill: "#2D5A27", r: 3 }} />
                    <Line type="monotone" dataKey="simpanan" stroke="#D48C45" strokeWidth={2.5} dot={{ fill: "#D48C45", r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </Card>

        <Card className="rounded-xl border-stone-soft p-6">
          <h3 className="font-display font-semibold text-ink">Distribusi Kategori</h3>
          <p className="text-xs text-ink/55">Berdasarkan transaksi 30 hari</p>
          {!loaded ? <EmptyState note="Memuat…" /> : !hasCat ? (
            <EmptyState note="Belum ada transaksi" />
          ) : (
            <>
              <div className="h-56 mt-2">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={catData} dataKey="value" nameKey="name" outerRadius={75} innerRadius={45} paddingAngle={2}>
                      {catData.map((_, i) => <Cell key={i} fill={pieColors[i % pieColors.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-1.5 mt-2">
                {catData.map((c, i) => (
                  <div key={c.name} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-sm" style={{ background: pieColors[i % pieColors.length] }}></span>
                      <span className="text-ink/75">{c.name}</span>
                    </div>
                    <span className="font-semibold text-ink">{Math.round((c.value / catTotal) * 100)}%</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>
      </div>

      {/* Pertumbuhan Anggota + Aktivitas Terkini */}
      <div className="grid lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2 rounded-xl border-stone-soft p-6">
          <h3 className="font-display font-semibold text-ink">Pertumbuhan Anggota</h3>
          <div className="mt-3">
            {!loaded ? <EmptyState height="h-52" note="Memuat…" /> : !hasGrowth ? (
              <EmptyState height="h-52" note="Belum ada anggota terdaftar" />
            ) : (
              <div className="h-52">
                <ResponsiveContainer>
                  <BarChart data={memberGrowth}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E6E2D8" vertical={false} />
                    <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#889285" }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#889285" }} />
                    <Tooltip />
                    <Bar dataKey="anggota" radius={[8, 8, 0, 0]} fill="#2D5A27" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </Card>

        <Card className="rounded-xl border-stone-soft p-6">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="text-amber-warm" size={16} />
            <h3 className="font-display font-semibold text-ink">Aktivitas Terkini</h3>
          </div>
          {!loaded ? <EmptyState note="Memuat…" /> : activity.length === 0 ? (
            <EmptyState note="Belum ada aktivitas" />
          ) : (
            <div className="space-y-3">
              {activity.map((a, i) => (
                <div key={i} className="flex gap-3 items-start" data-testid={`audit-${i}`}>
                  <div className="w-2 h-2 rounded-full bg-forest mt-2 shrink-0"></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-ink"><span className="font-semibold">{a.actor}</span> <span className="text-ink/55">· {a.role}</span></div>
                    <div className="text-xs text-ink/75 mt-0.5">{a.action}</div>
                    <div className="text-[10px] text-ink/55 mt-1">{a.time}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Pesanan Terbaru */}
      <Card className="rounded-xl border-stone-soft p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-semibold text-ink">Pesanan Terbaru</h3>
          <a className="text-sm text-forest font-semibold cursor-pointer">Lihat semua →</a>
        </div>
        {!loaded ? <EmptyState note="Memuat…" /> : recentOrders.length === 0 ? (
          <EmptyState note="Belum ada pesanan" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-cream text-ink/55 text-[10px] uppercase tracking-wider">
                  <th className="text-left p-3 rounded-l-lg">ID</th>
                  <th className="text-left p-3">Anggota</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Pembayaran</th>
                  <th className="text-right p-3 rounded-r-lg">Total</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.map((o) => (
                  <tr key={o.id} className="border-b border-stone-soft/60 hover:bg-cream/50" data-testid={`admin-recent-order-${o.id}`}>
                    <td className="p-3 font-mono text-xs font-semibold text-ink">{o.id}</td>
                    <td className="p-3">{o.member}</td>
                    <td className="p-3">
                      <Badge className={`border-0 rounded-full text-[10px] uppercase tracking-wider ${
                        o.status === "Selesai" || o.status === "Diterima" || o.status === "COMPLETED" || o.status === "DELIVERED" ? "bg-sage/20 text-forest hover:bg-sage/20" :
                        o.status === "Dikirim" || o.status === "SHIPPED" ? "bg-amber-warm/20 text-amber-700 hover:bg-amber-warm/20" :
                        o.status === "Menunggu Bayar" || o.status === "PENDING" ? "bg-terracotta/15 text-terracotta hover:bg-terracotta/15" :
                        "bg-stone-soft/40 text-ink/75 hover:bg-stone-soft/40"
                      }`}>{o.status}</Badge>
                    </td>
                    <td className="p-3 text-ink/75">{o.payment}</td>
                    <td className="p-3 text-right font-semibold">{formatRupiah(o.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

const Kpi = ({ icon: Icon, label, value, delta, warning }) => (
  <Card className="rounded-xl border-stone-soft p-5 bg-white" data-testid={`admin-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center justify-between mb-3">
      <div className="w-9 h-9 rounded-lg bg-cream grid place-items-center text-forest"><Icon size={17} /></div>
      <ArrowUpRight size={13} className="text-ink/55" />
    </div>
    <div className="text-[10px] uppercase tracking-[0.16em] text-ink/55 font-semibold">{label}</div>
    <div className="font-display font-bold text-2xl text-ink mt-1 truncate">{value}</div>
    <div className={`text-[11px] mt-1.5 flex items-center gap-1 ${warning ? "text-terracotta" : "text-forest"}`}>
      {warning ? <AlertCircle size={11} /> : <TrendingUp size={11} />} {delta}
    </div>
  </Card>
);
